import { Fragment, useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  api,
  ApiError,
  type LlmModel,
  type LlmModelPrice,
  type ReasoningEffort,
  type SystemPrompt,
} from "../api.js";
import { useToken } from "../auth.js";
import { GroupButton } from "../components/GroupButton.js";

// Models & prices page. One row per model; expand a row to see its price history and
// stage a new "effective_at" tariff. Prices are append-only — historical rows stay
// referenced by usage_records snapshots later on (Phase 5).
export function LlmModels() {
  const token = useToken();
  const { t, i18n } = useLingui();
  const [models, setModels] = useState<LlmModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<LlmModel | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [prices, setPrices] = useState<Record<string, LlmModelPrice[]>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pricingFor, setPricingFor] = useState<LlmModel | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listLlmModels(token);
      setModels(res.models);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const toggle = async (m: LlmModel) => {
    if (expanded === m.id) {
      setExpanded(null);
      return;
    }
    setExpanded(m.id);
    if (!prices[m.id]) {
      try {
        const res = await api.getLlmModel(token, m.id);
        setPrices((prev) => ({ ...prev, [m.id]: res.prices }));
      } catch (err) {
        alert(err instanceof ApiError ? err.message : String(err));
      }
    }
  };

  const deactivate = async (m: LlmModel) => {
    if (!confirm(t`Deactivate model "${m.displayName}"? Active plans referencing it must be reassigned first.`))
      return;
    setBusyId(m.id);
    try {
      await api.deactivateLlmModel(token, m.id);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  const reactivate = async (m: LlmModel) => {
    if (!confirm(t`Reactivate model "${m.displayName}"?`)) return;
    setBusyId(m.id);
    try {
      await api.reactivateLlmModel(token, m.id);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  const activeModels = useMemo(() => models.filter((m) => m.isActive), [models]);
  const inactiveModels = useMemo(() => models.filter((m) => !m.isActive), [models]);

  const renderRates = (price: Pick<LlmModelPrice,
    "priceInputPerMtok" | "priceCachedPerMtok" | "priceCacheWritePerMtok" | "priceOutputPerMtok">) => (
    (["priceInputPerMtok", "priceCachedPerMtok", "priceCacheWritePerMtok", "priceOutputPerMtok"] as const)
      .map(field => (
        <td key={field}>
          {price[field] === null ? "—" : `$${Number(price[field]).toLocaleString(i18n.locale, { maximumFractionDigits: 6 })}`}
        </td>
      ))
  );

  const renderRow = (m: LlmModel) => (
    <Fragment key={m.id}>
      <tr>
        <td>{m.provider}</td>
        <td>
          <code>{m.providerModelId}</code>
        </td>
        <td>{m.displayName}</td>
        <td>{m.contextWindow.toLocaleString(i18n.locale)}</td>
        <td>{m.maxOutputTokens.toLocaleString(i18n.locale)}</td>
        <td>{m.provider === "openai" ? m.reasoningEffort : "—"}</td>
        <td>
          <GroupButton
            label={<Trans>Edit</Trans>}
            dropdownLabel={t`Model actions`}
            onClick={() => setEditing(m)}
          >
            <button className="link-btn" onClick={() => void toggle(m)}>
              {expanded === m.id ? <Trans>Hide</Trans> : <Trans>Prices</Trans>}
            </button>
            <button className="link-btn" onClick={() => setEditing(m)}>
              <Trans>Edit</Trans>
            </button>
            {m.isActive ? (
              <>
                <button className="link-btn" onClick={() => setPricingFor(m)}>
                  <Trans>Add price</Trans>
                </button>
                <button
                  className="link-btn danger"
                  disabled={busyId === m.id}
                  onClick={() => void deactivate(m)}
                >
                  <Trans>Deactivate</Trans>
                </button>
              </>
            ) : (
              <button
                className="link-btn"
                disabled={busyId === m.id}
                onClick={() => void reactivate(m)}
              >
                <Trans>Reactivate</Trans>
              </button>
            )}
          </GroupButton>
        </td>
      </tr>
      {expanded === m.id && (
        <tr>
          <td colSpan={7}>
            {!prices[m.id] ? (
              <p className="muted"><Trans>Loading prices…</Trans></p>
            ) : prices[m.id].length === 0 ? (
              <p className="muted"><Trans>No price version yet.</Trans></p>
            ) : (
              <div>
                <p className="muted"><Trans>All rates are USD per million tokens. Empty cache rates use the input rate of the selected tier; zero means free.</Trans></p>
                <table className="table" style={{ marginTop: 8 }}>
                  <thead>
                    <tr>
                      <th><Trans>Effective at</Trans></th>
                      <th><Trans>Pricing tier</Trans></th>
                      <th><Trans>Input $/Mtok</Trans></th>
                      <th><Trans>Cached input $/Mtok</Trans></th>
                      <th><Trans>Cache write $/Mtok</Trans></th>
                      <th><Trans>Output $/Mtok</Trans></th>
                    </tr>
                  </thead>
                  <tbody>
                    {prices[m.id].map((p) => (
                      <Fragment key={p.id}>
                      <tr>
                        <td>{new Date(p.effectiveAt).toLocaleString(i18n.locale)}</td>
                        <td><Trans>Standard</Trans></td>
                        {renderRates(p)}
                      </tr>
                      {p.longContext && (
                        <tr>
                          <td>{new Date(p.effectiveAt).toLocaleString(i18n.locale)}</td>
                          <td><Trans>Long context: input &gt; {p.longContext.inputTokenThreshold.toLocaleString(i18n.locale)} tokens</Trans></td>
                          {renderRates(p.longContext)}
                        </tr>
                      )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </td>
        </tr>
      )}
    </Fragment>
  );

  const renderTable = (rows: LlmModel[]) => (
    <table className="table">
      <thead>
        <tr>
          <th><Trans>Provider</Trans></th>
          <th><Trans>Model id</Trans></th>
          <th><Trans>Display name</Trans></th>
          <th><Trans>Context</Trans></th>
          <th><Trans>Max output</Trans></th>
          <th><Trans>Reasoning</Trans></th>
          <th />
        </tr>
      </thead>
      <tbody>{rows.map(renderRow)}</tbody>
    </table>
  );

  return (
    <div>
      <header className="page-header">
        <h1><Trans>Models & prices</Trans></h1>
        <button onClick={() => setCreating(true)}><Trans>New model</Trans></button>
      </header>
      {error && <div className="banner err">{error}</div>}
      {loading ? (
        <p className="muted"><Trans>Loading…</Trans></p>
      ) : (
        <>
          {renderTable(activeModels)}
          {inactiveModels.length > 0 && (
            <>
              <h2 style={{ marginTop: 24 }}><Trans>Inactive models</Trans></h2>
              {renderTable(inactiveModels)}
            </>
          )}
        </>
      )}

      {creating && (
        <ModelEditor
          onCancel={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void refresh();
          }}
        />
      )}
      {editing && (
        <ModelEditor
          model={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      )}
      {pricingFor && (
        <NewPriceDialog
          model={pricingFor}
          onCancel={() => setPricingFor(null)}
          onSaved={async () => {
            const modelId = pricingFor.id;
            setPricingFor(null);
            setExpanded(modelId);
            // Refetch — deleting the cache entry alone would leave the row
            // stuck on "Loading prices…" because toggle() (which does the
            // fetch) is only wired to the expand click, not to state changes.
            try {
              const res = await api.getLlmModel(token, modelId);
              setPrices((prev) => ({ ...prev, [modelId]: res.prices }));
            } catch (err) {
              alert(err instanceof ApiError ? err.message : String(err));
            }
          }}
        />
      )}
    </div>
  );
}

// Shared editor for both "New model" and "Edit model". Provider and provider_model_id
// are locked in edit mode since they identify the row that pricing / usage snapshots
// point at — changing them would silently repoint historical data.
function ModelEditor({
  model,
  onCancel,
  onSaved,
}: {
  model?: LlmModel;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const token = useToken();
  const { t } = useLingui();
  const isEdit = !!model;
  const [provider, setProvider] = useState<"openai" | "anthropic">(
    model?.provider ?? "openai",
  );
  const [providerModelId, setProviderModelId] = useState(model?.providerModelId ?? "");
  const [displayName, setDisplayName] = useState(model?.displayName ?? "");
  const [contextWindow, setContextWindow] = useState(model?.contextWindow ?? 128000);
  const [systemPromptId, setSystemPromptId] = useState<string>(model?.systemPromptId ?? "");
  const [maxOutputTokens, setMaxOutputTokens] = useState(model?.maxOutputTokens ?? 4096);
  const [reasoningEffort, setReasoningEffort] = useState<ReasoningEffort>(
    model?.reasoningEffort ?? "medium",
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Load system prompts up front. Only active ones are offered for selection —
  // the current one stays visible even if it was archived, so an existing model
  // isn't silently reset.
  const [prompts, setPrompts] = useState<SystemPrompt[] | null>(null);
  const [promptsError, setPromptsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listSystemPrompts(token)
      .then((res) => {
        if (cancelled) return;
        setPrompts(res.prompts);
        if (!isEdit && !systemPromptId) {
          const firstActive = res.prompts.find((p) => p.isActive);
          if (firstActive) setSystemPromptId(firstActive.id);
        }
      })
      .catch((err) => {
        if (!cancelled)
          setPromptsError(err instanceof ApiError ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [token, isEdit, systemPromptId]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!systemPromptId) {
      setError(t`System prompt is required`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit) {
        await api.updateLlmModel(token, model!.id, {
          displayName,
          contextWindow,
          systemPromptId,
          maxOutputTokens,
          reasoningEffort,
        });
      } else {
        await api.createLlmModel(token, {
          provider,
          providerModelId,
          displayName,
          contextWindow,
          systemPromptId,
          maxOutputTokens,
          reasoningEffort,
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const promptOptions = useMemo(() => {
    if (!prompts) return [];
    const currentId = model?.systemPromptId;
    return prompts.filter((p) => p.isActive || p.id === currentId);
  }, [prompts, model?.systemPromptId]);

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? t`Edit ${model!.displayName}` : t`New model`}</h2>
        <form onSubmit={submit}>
          <label>
            <Trans>Provider</Trans>
            <select
              value={provider}
              disabled={isEdit}
              onChange={(e) => setProvider(e.target.value as "openai" | "anthropic")}
            >
              <option value="openai">openai</option>
              <option value="anthropic">anthropic</option>
            </select>
          </label>
          <label>
            <Trans>Provider model id</Trans>
            <input
              required
              value={providerModelId}
              disabled={isEdit}
              onChange={(e) => setProviderModelId(e.target.value)}
              placeholder="gpt-4o-mini or claude-haiku-4-5-20251001"
            />
          </label>
          <label>
            <Trans>Display name</Trans>
            <input
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </label>
          <label>
            <Trans>Context window (tokens)</Trans>
            <input
              type="number"
              min={1}
              value={contextWindow}
              onChange={(e) => setContextWindow(Number(e.target.value) || 1)}
            />
          </label>
          <label>
            <Trans>Max output tokens</Trans>
            <input
              type="number"
              min={1}
              value={maxOutputTokens}
              onChange={(e) => setMaxOutputTokens(Number(e.target.value) || 1)}
            />
          </label>
          <label>
            <Trans>Reasoning effort</Trans>
            <select
              value={reasoningEffort}
              onChange={(e) => setReasoningEffort(e.target.value as ReasoningEffort)}
            >
              <option value="none">{t`None — lowest latency`}</option>
              <option value="low">{t`Low`}</option>
              <option value="medium">{t`Medium — recommended`}</option>
              <option value="high">{t`High`}</option>
              <option value="xhigh">{t`Extra high`}</option>
              <option value="max">{t`Maximum`}</option>
            </select>
            <small className="muted">
              <Trans>Only applied to OpenAI models; Anthropic ignores this field.</Trans>
            </small>
          </label>
          <label>
            <Trans>System prompt</Trans>
            {promptsError ? (
              <div className="banner err">{promptsError}</div>
            ) : prompts === null ? (
              <p className="muted"><Trans>Loading system prompts…</Trans></p>
            ) : (
              <select
                required
                value={systemPromptId}
                onChange={(e) => setSystemPromptId(e.target.value)}
              >
                <option value="" disabled>
                  {t`Select a system prompt…`}
                </option>
                {promptOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.isActive ? p.label : t`${p.label} (archived)`}
                  </option>
                ))}
              </select>
            )}
            <small className="muted">
              <Link to="/system-prompts"><Trans>Manage system prompts</Trans></Link>
            </small>
          </label>
          {error && <div className="banner err">{error}</div>}
          <div className="actions">
            <button type="button" className="ghost" onClick={onCancel}>
              <Trans>Cancel</Trans>
            </button>
            <button type="submit" disabled={submitting}>
              {submitting ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NewPriceDialog({
  model,
  onCancel,
  onSaved,
}: {
  model: LlmModel;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const token = useToken();
  const { t } = useLingui();
  const [effectiveAt, setEffectiveAt] = useState(new Date().toISOString());
  const [priceInputPerMtok, setPriceInputPerMtok] = useState(0);
  const [priceCachedPerMtok, setPriceCachedPerMtok] = useState<number | "">("");
  const [priceCacheWritePerMtok, setPriceCacheWritePerMtok] = useState<number | "">("");
  const [priceOutputPerMtok, setPriceOutputPerMtok] = useState(0);
  const [longContextEnabled, setLongContextEnabled] = useState(false);
  const [inputTokenThreshold, setInputTokenThreshold] = useState<number | "">("");
  const [longInput, setLongInput] = useState<number | "">("");
  const [longCached, setLongCached] = useState<number | "">("");
  const [longWrite, setLongWrite] = useState<number | "">("");
  const [longOutput, setLongOutput] = useState<number | "">("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.createLlmModelPrice(token, model.id, {
        effectiveAt,
        priceInputPerMtok,
        priceCachedPerMtok: priceCachedPerMtok === "" ? undefined : priceCachedPerMtok,
        priceCacheWritePerMtok:
          priceCacheWritePerMtok === "" ? undefined : priceCacheWritePerMtok,
        priceOutputPerMtok,
        longContext: longContextEnabled ? {
          inputTokenThreshold: Number(inputTokenThreshold),
          priceInputPerMtok: Number(longInput),
          priceCachedPerMtok: longCached === "" ? undefined : longCached,
          priceCacheWritePerMtok: longWrite === "" ? undefined : longWrite,
          priceOutputPerMtok: Number(longOutput),
        } : null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{t`New price for ${model.displayName}`}</h2>
        <form onSubmit={submit}>
          <label>
            <Trans>Effective at (ISO 8601)</Trans>
            <input
              required
              value={effectiveAt}
              onChange={(e) => setEffectiveAt(e.target.value)}
            />
          </label>
          <label>
            <Trans>Input $/1M tokens</Trans>
            <input
              type="number"
              min={0}
              step={0.001}
              value={priceInputPerMtok}
              onChange={(e) => setPriceInputPerMtok(Number(e.target.value) || 0)}
              required
            />
          </label>
          <label>
            <Trans>Cached input $/1M tokens (optional)</Trans>
            <input
              type="number"
              min={0}
              step={0.001}
              value={priceCachedPerMtok}
              onChange={(e) =>
                setPriceCachedPerMtok(e.target.value === "" ? "" : Number(e.target.value))
              }
            />
          </label>
          <label>
            <Trans>Cache write $/1M tokens (optional — set for models with a cache-write premium; leave empty to bill at the input rate)</Trans>
            <input
              type="number"
              min={0}
              step={0.001}
              value={priceCacheWritePerMtok}
              onChange={(e) =>
                setPriceCacheWritePerMtok(
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
            />
          </label>
          <label>
            <Trans>Output $/1M tokens</Trans>
            <input
              type="number"
              min={0}
              step={0.001}
              value={priceOutputPerMtok}
              onChange={(e) => setPriceOutputPerMtok(Number(e.target.value) || 0)}
              required
            />
          </label>
          <p className="muted"><Trans>All rates are USD per million tokens. Empty cache rates use the input rate of the selected tier; zero means free.</Trans></p>
          <label className="inline">
            <input type="checkbox" checked={longContextEnabled} onChange={e => setLongContextEnabled(e.target.checked)} />
            <Trans>Enable long-context pricing</Trans>
          </label>
          {longContextEnabled && (
            <fieldset>
              <legend><Trans>Long-context tier</Trans></legend>
              <p className="muted"><Trans>Applies to the whole request only when total input, including cache reads and writes, strictly exceeds the threshold.</Trans></p>
              <label>
                <Trans>Input token threshold</Trans>
                <input type="number" min={1} max={2147483647} step={1} required value={inputTokenThreshold}
                  onChange={e => setInputTokenThreshold(e.target.value === "" ? "" : Number(e.target.value))} />
              </label>
              <label>
                <Trans>Input $/1M tokens</Trans>
                <input type="number" min={0} max={10000} step={0.000001} required value={longInput}
                  onChange={e => setLongInput(e.target.value === "" ? "" : Number(e.target.value))} />
              </label>
              <label>
                <Trans>Cached input $/1M tokens (optional)</Trans>
                <input type="number" min={0} max={10000} step={0.000001} value={longCached}
                  onChange={e => setLongCached(e.target.value === "" ? "" : Number(e.target.value))} />
              </label>
              <label>
                <Trans>Cache write $/1M tokens (optional)</Trans>
                <input type="number" min={0} max={10000} step={0.000001} value={longWrite}
                  onChange={e => setLongWrite(e.target.value === "" ? "" : Number(e.target.value))} />
              </label>
              <label>
                <Trans>Output $/1M tokens</Trans>
                <input type="number" min={0} max={10000} step={0.000001} required value={longOutput}
                  onChange={e => setLongOutput(e.target.value === "" ? "" : Number(e.target.value))} />
              </label>
            </fieldset>
          )}
          {error && <div className="banner err">{error}</div>}
          <div className="actions">
            <button type="button" className="ghost" onClick={onCancel}>
              <Trans>Cancel</Trans>
            </button>
            <button type="submit" disabled={submitting}>
              {submitting ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
